import {
  type MatchState,
  type MatchTelemetryRow,
  type PlayerView,
  type TelemetrySummary,
  buildTelemetryRow,
  summarizeRows,
} from '@signalclash/shared';

// V1.5 playtest telemetry — browser-side persistence layer. The pure row
// shape and aggregator now live in @signalclash/shared so the headless
// runner (tools/playtest-auto.ts) can produce / consume the same row
// without dragging in DOM or localStorage. This module re-exports those
// pure helpers and keeps the localStorage round-trip + the dev-console
// glue here, where DOM access is allowed.
//
// Why client-side localStorage:
//   - V1.5 has no server. We can't ship telemetry to a backend yet.
//   - Solo dev / solo playtester loop: paste localStorage contents into a
//     spreadsheet after a session.
//   - Schema versioning: bump the storage key suffix when the shape
//     changes; older entries are silently dropped on parse error.
//
// Storage key:    signalclash.telemetry.v1
// Capacity:       MAX_TELEMETRY_ROWS (FIFO once full)

export type { MatchTelemetryRow, TelemetrySummary };
// Backwards-compatible name kept for the dev-console hook in ClientApp;
// the canonical name now lives in shared as `summarizeRows`.
export const summarize = summarizeRows;

const STORAGE_KEY = 'signalclash.telemetry.v1';
export const MAX_TELEMETRY_ROWS = 100;

export function recordMatch(snapshot: PlayerView | MatchState): void {
  if (snapshot.outcome === null) return;
  const row = buildTelemetryRow(snapshot);
  const list = loadRowsRaw();
  list.push(row);
  while (list.length > MAX_TELEMETRY_ROWS) list.shift();
  saveRowsRaw(list);
}

export function loadRows(): MatchTelemetryRow[] {
  return loadRowsRaw();
}

export function clearTelemetry(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage disabled — no-op.
  }
}

function loadRowsRaw(): MatchTelemetryRow[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPlausibleRow);
  } catch {
    return [];
  }
}

function saveRowsRaw(rows: MatchTelemetryRow[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // Storage full / disabled — telemetry stays session-local.
  }
}

function isPlausibleRow(v: unknown): v is MatchTelemetryRow {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.recordedAt === 'string' &&
    typeof o.matchSeconds === 'number' &&
    Array.isArray(o.doctrines) &&
    Array.isArray(o.cachesPickedByPlayer)
  );
}
